import { isPlatformServer, ViewportScroller } from '@angular/common';
import { EnvironmentProviders, makeEnvironmentProviders, APP_INITIALIZER, PLATFORM_ID } from '@angular/core';
import { Router, Scroll, NavigationStart } from '@angular/router';
import { filter, pairwise } from 'rxjs/operators';

/**
 * Use only `provideScrollRestoration` only if  `scrollPositionRestoration` is disabled and
 *  outside the `provideRouter`.
 *
 * This is mostly used to restore the scroll without updating it when the params of a URL has changed, like on Catalogue pages.
 *
 * Example: Panoptico `app.config.ts`
 *
 * provideRouter(
 *    ...,
 *    withInMemoryScrolling({
 *      anchorScrolling: 'enabled',
 *      scrollPositionRestoration: 'disabled'
 *    }),
 *  ),
 * provideScrollRestoration()
 * */
export function provideScrollRestoration(): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: APP_INITIALIZER,
      useFactory: (router: Router, viewportScroller: ViewportScroller, platformId) => () => {
        // console.info('✅ Scroll Restoration Provider Initialized (APP_INITIALIZER)');

        let lastScrollPosition = 0;

        if (isPlatformServer(platformId)) {
          return Promise.resolve();
        }

        // ✅ Detect Query Param Changes
        router.events
          .pipe(
            filter((event): event is NavigationStart => event instanceof NavigationStart),
            pairwise() // Get previous & current navigation events
          )
          .subscribe(([prev, curr]) => {
            const prevUrl = prev.url.split('?')[0]; // Remove query params
            const currUrl = curr.url.split('?')[0];

            if (prevUrl === currUrl) {
              console.log('Query param update detected, preventing scroll reset');
              lastScrollPosition = window.scrollY;
            }
          });

        // ✅ Handle Scroll Restoration
        router.events.pipe(filter((event): event is Scroll => event instanceof Scroll)).subscribe(event => {
          if (event.position) {
            // ✅ Restore previous scroll position for normal navigation
            viewportScroller.scrollToPosition(event.position);
          } else if (event.anchor) {
            // ✅ Scroll to anchor if available
            viewportScroller.scrollToAnchor(event.anchor);
          } else {
            // This line will exclude scroll to top if pagination is applied, eg: load more button
            const isPaginationEnabled = event.routerEvent.url.includes('page=');
            // ✅ Default to scrolling to top if no anchor
            viewportScroller.scrollToPosition([0, isPaginationEnabled ? lastScrollPosition : 0]);
          }
        });

        return Promise.resolve();
      },
      multi: true,
      deps: [Router, ViewportScroller, PLATFORM_ID]
    }
  ]);
}
